import React, { useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Plus, Minus, ShoppingBag, MessageCircle } from "lucide-react";
import { getProduct, getRelated } from "@/data/products";
import { useProduct, getCategoryLabel } from "@/lib/useProducts";
import SEO, { productSchema, breadcrumbSchema, faqSchema } from "@/components/SEO";
import BuyNowModal from "@/components/BuyNowModal";
import { useCart } from "@/lib/CartContext";
import { buildWhatsAppUrl, formatPrice, priceForSize } from "@/lib/brand";
import { trackClick } from "@/lib/analytics";

const Spec = ({ label, value }) => (
  <div className="grid grid-cols-2 gap-4 py-3 border-b border-[#3B2A1A]/10 text-sm">
    <span className="text-[#8A7969] uppercase tracking-[0.15em] text-[11px]">{label}</span>
    <span className="text-[#3B2A1A] font-light">{value}</span>
  </div>
);

const ProductDetail = () => {
  const { slug } = useParams();
  const { product: apiProduct, loading } = useProduct(slug);
  const product = apiProduct || getProduct(slug);
  const [activeImg, setActiveImg] = useState(0);
  const [openFaq, setOpenFaq] = useState(null);
  const [buyOpen, setBuyOpen] = useState(false);
  const { addItem } = useCart();
  const [selSize, setSelSize] = useState("");
  const [selQty, setSelQty] = useState(1);

  if (loading && !product) return <div className="min-h-screen" />;
  if (!product) return <Navigate to="/products" replace />;
  const related = getRelated(slug);
  const gallery = [product.image, product.image, product.image, product.image];
  const activeSize = selSize || (product.sizes && product.sizes[0]) || "";
  const rawPrice = priceForSize(product, activeSize);
  const displayPrice = formatPrice(rawPrice);
  const hasMultipleVariants = product.variant_prices && Object.keys(product.variant_prices).length > 0;

  const handleAddToCart = () => {
    addItem({
      slug: product.slug,
      name: product.name,
      size: activeSize,
      qty: selQty,
      price: displayPrice,
      image: product.image,
    });
    trackClick(`add-to-cart-${product.slug}`);
  };

  const handleWhatsAppOne = () => {
    const msg = `Namaste 🙏\n\nI'd like to order:\n• ${product.name}${activeSize ? ` (${activeSize})` : ""} × ${selQty}${displayPrice ? `  — ${displayPrice}` : ""}\n\nPlease confirm availability and delivery.`;
    trackClick(`whatsapp-product-${product.slug}`);
    window.open(buildWhatsAppUrl(msg), "_blank", "noopener,noreferrer");
  };

  return (
    <div data-testid="product-detail-page">
      <SEO
        title={`Buy ${product.name} Online — Sanatan Dharma`}
        description={`${product.short} Available in ${(product.sizes || []).join(", ")}. Order authentic ${product.name} from Sanatan Dharma — trusted premium quality with pan-India delivery.`}
        keywords={`buy ${product.name.toLowerCase()} online, ${product.name.toLowerCase()} price, ${product.name.toLowerCase()} india, sanatan dharma ${product.name.toLowerCase()}, ${getCategoryLabel(product.category).toLowerCase()}, puja items online`}
        path={`/products/${product.slug}`}
        image={product.image.startsWith("http") ? product.image : `https://sanatandharmaonline.com${product.image}`}
        jsonLd={[
          productSchema(product),
          faqSchema(product.faq || []),
          breadcrumbSchema([
            { name: "Home", path: "/" },
            { name: "Products", path: "/products" },
            { name: product.name, path: `/products/${product.slug}` },
          ]),
        ]}
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10">
        <Link to="/products" className="text-xs uppercase tracking-[0.18em] text-[#8A7969] hover:text-[#3B2A1A] inline-flex items-center gap-2" data-testid="back-to-products">
          <ArrowLeft size={12} /> All Products
        </Link>
      </div>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-7">
            <div className="aspect-square overflow-hidden bg-[#F5EFE3] border border-[#3B2A1A]/10" data-testid="product-main-image">
              <img src={gallery[activeImg]} alt={product.name} className="w-full h-full object-cover" />
            </div>
            <div className="grid grid-cols-4 gap-3 mt-4">
              {gallery.map((g, i) => (
                <button
                  key={i}
                  onClick={() => setActiveImg(i)}
                  className={`aspect-square overflow-hidden border bg-[#F5EFE3] ${activeImg === i ? "border-[#C89D3C]" : "border-[#3B2A1A]/10"}`}
                  data-testid={`product-thumb-${i}`}
                >
                  <img src={g} alt="" className="w-full h-full object-cover opacity-80" />
                </button>
              ))}
            </div>
          </div>

          <div className="lg:col-span-5">
            <p className="overline" data-testid="product-category">
              {product.category === "books" ? "Books" : product.category === "daily" ? "Daily Essentials" : "Ritual Essentials"}
            </p>
            <h1 className="font-serif-display text-5xl lg:text-6xl text-[#3B2A1A] mt-3 leading-tight" data-testid="product-name">{product.name}</h1>
            <p className="text-[#C89D3C] italic font-serif-display text-xl mt-2">{product.tagline}</p>
            <p className="text-base leading-relaxed text-[#5C4A3A] mt-6 font-light">{product.short}</p>
            {displayPrice && (
              <div className="mt-6 flex items-baseline gap-3" data-testid="product-price-row">
                <p
                  key={activeSize || "base"}
                  className="font-serif-display text-3xl text-[#3B2A1A] transition-all"
                  data-testid="product-price"
                >
                  {displayPrice}
                </p>
                {activeSize && hasMultipleVariants && (
                  <span className="text-xs uppercase tracking-[0.18em] text-[#8A7969] font-semibold" data-testid="product-price-size">
                    · {activeSize}
                  </span>
                )}
              </div>
            )}

            {(product.sizes && product.sizes.length > 0) && (
              <div className="mt-8 border-t border-[#3B2A1A]/10 pt-6" data-testid="product-size-selector">
                <p className="overline text-[10px] mb-3">Choose Size</p>
                <div className="flex flex-wrap gap-2">
                  {product.sizes.map((s) => (
                    <button
                      key={s}
                      onClick={() => setSelSize(s)}
                      data-testid={`size-opt-${s}`}
                      className={`px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium border transition-colors ${
                        activeSize === s
                          ? "bg-[#3B2A1A] text-[#FBF6EC] border-[#3B2A1A]"
                          : "border-[#3B2A1A]/25 text-[#3B2A1A] hover:border-[#C89D3C] hover:text-[#C89D3C]"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {product.packaging && (
              <div className="mt-6" data-testid="product-quick-info">
                <p className="overline text-[10px] mb-1">Packaging</p>
                <p className="text-sm text-[#5C4A3A] font-light">{product.packaging}</p>
              </div>
            )}

            <div className="mt-6 flex items-center gap-6" data-testid="product-qty-selector">
              <div>
                <p className="overline text-[10px] mb-2">Quantity</p>
                <div className="inline-flex items-center border border-[#3B2A1A]/25">
                  <button onClick={() => setSelQty((q) => Math.max(1, q - 1))} data-testid="pd-qty-minus" className="p-2.5 text-[#3B2A1A] hover:bg-[#F5EFE3]"><Minus size={14} /></button>
                  <span className="px-4 text-sm font-medium text-[#3B2A1A]" data-testid="pd-qty-value">{selQty}</span>
                  <button onClick={() => setSelQty((q) => q + 1)} data-testid="pd-qty-plus" className="p-2.5 text-[#3B2A1A] hover:bg-[#F5EFE3]"><Plus size={14} /></button>
                </div>
              </div>
            </div>

            <div className="mt-10">
              <p className="overline mb-4">Benefits</p>
              <ul className="space-y-2.5">
                {product.benefits.map((b) => (
                  <li key={b} className="text-sm text-[#3B2A1A] flex items-start gap-3" data-testid={`benefit-${b}`}>
                    <span className="w-1.5 h-1.5 bg-[#C89D3C] mt-2 rounded-full" />
                    {b}
                  </li>
                ))}
              </ul>
            </div>

            <div className="mt-10">
              <p className="overline mb-4">How To Use</p>
              <ol className="space-y-3">
                {product.howTo.map((s, i) => (
                  <li key={i} className="text-sm text-[#5C4A3A] flex items-start gap-4 font-light" data-testid={`step-${i}`}>
                    <span className="font-serif-display text-2xl text-[#C89D3C] leading-none">{i + 1}</span>
                    <span className="pt-1">{s}</span>
                  </li>
                ))}
              </ol>
            </div>

            <div className="mt-12 flex flex-wrap gap-3">
              <button onClick={handleAddToCart} className="btn-primary" data-testid="add-to-cart-btn">
                Add to Cart <ShoppingBag size={14} />
              </button>
              <button onClick={handleWhatsAppOne} data-testid="pd-whatsapp-btn"
                className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-[#1EBE57] text-white px-6 py-3 text-xs uppercase tracking-[0.18em] font-semibold transition-colors">
                <MessageCircle size={14} /> WhatsApp Order
              </button>
              <button onClick={() => { setBuyOpen(true); trackClick(`buy-now-${product.slug}`); }} className="btn-outline" data-testid="buy-now-btn">
                Buy Now <ArrowRight size={14} />
              </button>
            </div>
          </div>
        </div>
      </section>

      <BuyNowModal product={product} open={buyOpen} onClose={() => setBuyOpen(false)} />

      <section className="py-16 lg:py-20 bg-[#F5EFE3] border-y border-[#3B2A1A]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <p className="overline mb-3">Specifications</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#3B2A1A]">The fine print, made beautiful.</h2>
          </div>
          <div className="lg:col-span-7" data-testid="product-specs">
            {Object.entries(product.specs).map(([k, v]) => (
              <Spec key={k} label={k} value={v} />
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 lg:py-24">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="overline mb-3">Frequently Asked</p>
          <h2 className="font-serif-display text-3xl lg:text-4xl text-[#3B2A1A]">Honest answers, plainly stated.</h2>
          <div className="mt-10 divide-y divide-[#3B2A1A]/10 border-y border-[#3B2A1A]/10" data-testid="product-faq">
            {product.faq.map((f, i) => (
              <button
                key={i}
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                className="w-full text-left py-5 flex justify-between items-start gap-4"
                data-testid={`faq-${i}`}
              >
                <div className="flex-1">
                  <p className="font-serif-display text-xl text-[#3B2A1A]">{f.q}</p>
                  {openFaq === i && <p className="text-sm text-[#5C4A3A] mt-3 font-light leading-relaxed">{f.a}</p>}
                </div>
                {openFaq === i ? <Minus size={18} className="mt-2 text-[#C89D3C]" /> : <Plus size={18} className="mt-2 text-[#C89D3C]" />}
              </button>
            ))}
          </div>
        </div>
      </section>

      {related.length > 0 && (
        <section className="py-16 lg:py-24 border-t border-[#3B2A1A]/10">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <p className="overline mb-3">You May Also Like</p>
            <h2 className="font-serif-display text-3xl lg:text-4xl text-[#3B2A1A]">Pairs beautifully with —</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-px bg-[#3B2A1A]/10 mt-10 border border-[#3B2A1A]/10" data-testid="related-products">
              {related.map((p) => (
                <Link to={`/products/${p.slug}`} key={p.slug} className="group bg-white p-5 sm:p-7 hover:bg-[#FBF6EC]/40 transition-colors" data-testid={`related-${p.slug}`}>
                  <div className="aspect-square overflow-hidden bg-[#F5EFE3] mb-4">
                    <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-[1200ms]" />
                  </div>
                  <h3 className="font-serif-display text-xl text-[#3B2A1A]">{p.name}</h3>
                  <p className="text-xs text-[#8A7969] mt-1 font-light">{p.tagline}</p>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default ProductDetail;
